import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { cn } from '@/lib/utils'
import { chipView } from '../store/sync-view'
import { useUi } from '../store/ui'

interface SyncChipProps {
  /** Phones: the dot and a count only; the full sentence stays for assistive tech. */
  compact?: boolean
  className?: string
}

const STATUS_CHANGE_MS = 350

/**
 * Keeps the label that is leaving for one status-change beat so both sides of the crossfade
 * exist in the DOM. State is adjusted during render (React's pattern for derived state) and
 * cleared by a timer.
 */
function useLeaving(label: string): string | null {
  const [state, setState] = useState<{ current: string; leaving: string | null }>({
    current: label,
    leaving: null,
  })
  if (state.current !== label) {
    setState({ current: label, leaving: state.current })
  }
  useEffect(() => {
    if (state.leaving === null) return
    const handle = setTimeout(() => setState((s) => ({ ...s, leaving: null })), STATUS_CHANGE_MS)
    return () => clearTimeout(handle)
  }, [state.leaving])
  return state.current === label ? state.leaving : null
}

/**
 * One calm indicator, five worded states (CONTENT.md section 1). The dot is ink; it takes
 * red only for "masalah sinkron" and breathes only while the worker is syncing. Label
 * changes crossfade over 350 ms; the chip links to Sinkronisasi and is a polite live region.
 */
export function SyncChip({ compact = false, className }: SyncChipProps) {
  const sync = useUi((s) => s.sync)
  const online = useUi((s) => s.online)
  const view = chipView(sync, online)
  const leaving = useLeaving(view.label)

  const dot = (
    <span
      aria-hidden="true"
      className={cn(
        'size-2 shrink-0 rounded-sm',
        view.merah ? 'bg-primary' : 'bg-text',
        view.berjalan && 'sync-dot',
      )}
    />
  )

  if (compact) {
    return (
      <Link
        to="/sinkron"
        aria-label={view.label}
        aria-live="polite"
        data-status={view.status}
        className={cn(
          'flex h-11 min-w-11 items-center justify-center px-2 focus-settle',
          className,
        )}
      >
        <span className="inline-flex h-7 items-center gap-1.5 rounded-sm border border-ink-soft px-2 text-label font-medium text-text num">
          {dot}
          {view.count !== null ? <span aria-hidden="true">{view.count}</span> : null}
        </span>
        <span className="sr-only">{view.label}</span>
      </Link>
    )
  }

  return (
    <Link
      to="/sinkron"
      aria-label={view.label}
      aria-live="polite"
      data-status={view.status}
      className={cn(
        'inline-flex h-7 items-center gap-2 rounded-sm border border-ink-soft bg-bg px-2 text-label font-medium text-text num hover-band focus-settle pointer-coarse:h-11',
        className,
      )}
    >
      {dot}
      <span className="grid [&>*]:col-start-1 [&>*]:row-start-1">
        {leaving !== null ? (
          <span aria-hidden="true" className="status-out whitespace-nowrap">
            {leaving}
          </span>
        ) : null}
        <span key={view.label} className={cn('whitespace-nowrap', leaving !== null && 'status-in')}>
          {view.label}
        </span>
      </span>
    </Link>
  )
}
